import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import {
  LineLiveCadence,
  LineLiveSnapshot,
} from "../../models/live-production.model";

/**
 * Ligne ultra-compacte (monoline) pour le widget dashboard. Affiche pastille,
 * nom, OF + conducteur (sous-titre tiny), cadence, qty + progress.
 */
@Component({
  selector: "vl-live-line-row",
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  templateUrl: "./live-line-row.component.html",
  styleUrl: "./live-line-row.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LiveLineRowComponent {
  ligne = input.required<LineLiveSnapshot>();
  cadence = input<LineLiveCadence | null>(null);

  open = output<LineLiveSnapshot>();

  cadenceUH = computed(() => {
    const c = this.cadence();
    return c?.cadenceInstantUH ?? null;
  });

  cadenceVsCible = computed<"high" | "mid" | "low" | "neutral">(() => {
    const v = this.cadenceUH();
    const cible = this.ligne().cadenceCibleUH;
    if (v == null || cible == null || cible === 0) return "neutral";
    const ratio = v / cible;
    if (ratio >= 0.9) return "high";
    if (ratio >= 0.6) return "mid";
    return "low";
  });

  progressPct = computed(() => {
    const o = this.ligne().currentOf;
    if (!o || !o.quantite) return 0;
    return Math.min(100, (o.quantiteProduite / o.quantite) * 100);
  });

  conducteursLabel = computed(() => {
    const cs = this.ligne().currentOf?.conducteurs ?? [];
    if (cs.length === 0) return "";
    if (cs.length === 1) return cs[0].denomination ?? cs[0].initiales;
    return `${cs[0].initiales} +${cs.length - 1}`;
  });

  /** Couleur déterministe pour la pastille initiales du 1er conducteur. */
  conducteurColor = computed(() => {
    const cs = this.ligne().currentOf?.conducteurs ?? [];
    if (cs.length === 0) return null;
    return colorFromId(cs[0].userInfosId);
  });

  initiales = computed(() => {
    const cs = this.ligne().currentOf?.conducteurs ?? [];
    return cs[0]?.initiales ?? null;
  });

  stateClass = computed(() => `state-${this.ligne().state.toLowerCase()}`);

  stateLabel = computed(() => {
    switch (this.ligne().state) {
      case "EN_PROD":
        return "En production";
      case "EN_TRANSITION":
        return "En transition";
      case "EN_ATTENTE":
        return "En attente";
      case "HORS_PLAGE":
        return "Hors plage";
      case "DEVICE_OFFLINE":
        return "Hors ligne";
      default:
        return "Inactif";
    }
  });

  /** Tooltip pédagogique expliquant le critère de l'état. */
  stateTooltip = computed(() => {
    switch (this.ligne().state) {
      case "EN_PROD":
        return "En production : OF actif avec une opération démarrée.";
      case "EN_TRANSITION":
        return "En transition : OF actif mais aucune opé en cours (entre deux opés).";
      case "EN_ATTENTE":
        return "En attente : un OF est planifié mais pas encore démarré.";
      case "HORS_PLAGE":
        return "Hors plage : aucun horaire ouvert actuellement.";
      case "DEVICE_OFFLINE":
        return "Hors ligne : automate sans trame depuis > 5 min.";
      default:
        return "Inactif : aucun OF actif ou planifié.";
    }
  });

  /** Délai humain pour le prochain OF si en attente. */
  nextOfDelayLabel = computed(() => {
    const next = this.ligne().nextOf;
    if (!next?.delayMinutes) return null;
    const m = next.delayMinutes;
    if (m < 60) return `dans ${m} min`;
    const h = Math.floor(m / 60);
    const rest = m % 60;
    return rest === 0 ? `dans ${h} h` : `dans ${h}h${String(rest).padStart(2, "0")}`;
  });

  reprisePrevueLabel = computed(() => {
    const iso = this.ligne().reprisePrevue;
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  });

  /** Cadence instantanée > 0 → la ligne produit activement. Glow vert. */
  isRunning = computed(() => {
    const v = this.cadence()?.cadenceInstantUH;
    return v != null && v > 0;
  });

  /** Best score : durée sans arrêt courante ≥ record session → reflet doré. */
  isBestScore = computed(() => {
    const cur = this.cadence()?.dureeSansArretSec;
    const max = this.cadence()?.dureeMaxSansArretSec;
    if (cur == null || max == null || max === 0) return false;
    return cur >= max;
  });

  /** Présence d'un automate connecté à la ligne. */
  hasMachine = computed(() => !!this.ligne().deviceId);

  /** Statut connexion automate (null = pas d'automate). */
  deviceOnline = computed<boolean | null>(() => {
    if (!this.hasMachine()) return null;
    return this.ligne().state !== "DEVICE_OFFLINE";
  });

  /** Format compact de l'étape en cours : "Saisie de la traçabilité (4/4)". */
  etapeShortLabel = computed(() => {
    const o = this.ligne().currentOf;
    if (!o) return null;
    const e = o.currentEtape;
    const ope = o.activePosteDenomination;
    if (e && ope) return `${ope} · ${e.denomination}`;
    if (e) return e.denomination;
    if (ope) return ope;
    return null;
  });

  /** Tooltip avec numéro d'étape (X/Y). */
  etapeFullLabel = computed(() => {
    const o = this.ligne().currentOf;
    if (!o) return "";
    const e = o.currentEtape;
    const ope = o.activePosteDenomination;
    if (e && ope) return `${ope} — Étape ${e.index}/${e.total} : ${e.denomination}`;
    if (e) return `Étape ${e.index}/${e.total} : ${e.denomination}`;
    return ope ?? "";
  });

  onClick() {
    this.open.emit(this.ligne());
  }
}

/** Hash déterministe minimaliste pour mapper un id à une couleur stable. */
function colorFromId(id: string): string {
  // 8 teintes pastel équilibrées (≈ 360°/8)
  const hues = [199, 270, 320, 20, 50, 100, 150, 230];
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = (h * 31 + id.charCodeAt(i)) >>> 0;
  }
  const hue = hues[h % hues.length];
  return `hsl(${hue} 60% 55%)`;
}
